import { FC, ReactNode, useEffect } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CheckIcon } from '../../../../../assets/icons/index';
import Button, { ButtonDisableStyle } from '../../../../../components/button/index';
import { FormInput } from '../../../../../components/input/index';
import { FormRadio, FormRadioGroup } from '../../../../../components/radio-group/index';
import { useReactForm } from '../../../../../hooks/index';
import {
  perkClaimDisplayRuleMap,
  perkClaimDisplayRuleTranslationKey,
  PerkClaimType,
  perkClaimTypeMap,
  perkClaimTypeTranslationKey,
  perkOfferTranslationKey,
  perkOfferTypeOrderMap,
} from '../../perks.types';
import { FormControlButtons } from '../components/form-control-buttons';
import { PerkFormSchema, perkFormSchema } from '../form.validation';
import { PerkStepFormProps } from './steps.types';

type SubContent = {
  control: Control<PerkFormSchema['stepFour']>;
};

const PromoCodeSubContent: FC<SubContent> = ({ control }) => {
  const displayRule = useWatch({ control, name: 'claim.displayRule' });

  return (
    <div className="flex w-full flex-col gap-3 rounded-md bg-white px-4 py-3">
      <FormInput control={control} name="claim.code" placeholder="Code" wrapperClassName="w-full" />
      <div className="h-[1px] w-full bg-gray-100" />
      <FormRadioGroup className="gap-4" control={control} name="claim.displayRule">
        {perkClaimDisplayRuleMap.map((variant) => (
          <FormRadio
            className="flex flex-col items-start gap-3 rounded-md border border-gray-100 bg-white !p-1 data-[checked]:border-transparent data-[checked]:bg-brand-25"
            control={control}
            key={variant}
            name="claim.displayRule"
            value={variant}
          >
            <div className="flex items-center gap-3 p-1 text-sm font-[450] text-gray-700">
              <div
                className={twMerge(
                  'flex size-4 items-center justify-center rounded-full border border-gray-300',
                  displayRule === variant && 'border-transparent bg-brand-700',
                )}
              >
                {displayRule === variant && <CheckIcon className="size-2" iconColor="#ffffff" />}
              </div>
              <span className="text-nowrap text-sm font-[450] text-gray-700">
                {perkClaimDisplayRuleTranslationKey[variant]}
              </span>
            </div>
          </FormRadio>
        ))}
      </FormRadioGroup>
    </div>
  );
};

const LinkSubContent: FC<SubContent> = ({ control }) => (
  <div className="w-full rounded-md bg-white px-4 py-3">
    <FormInput control={control} name="claim.url" placeholder="URL" wrapperClassName="w-full" />
  </div>
);

const claimSubContent: Record<PerkClaimType, (props: SubContent) => ReactNode> = {
  [PerkClaimType.PROMO_CODE]: (props) => <PromoCodeSubContent {...props} />,
  [PerkClaimType.LINK]: (props) => <LinkSubContent {...props} />,
};

export const StepFour: FC<PerkStepFormProps> = ({
  onSubmit,
  handlePrevStep,
  patchStep,
  defaultData: { stepFour },
}) => {
  const {
    control,
    formState: { isValid },
    handleSubmit,
    setValue,
    reset,
  } = useReactForm({
    schema: perkFormSchema.shape.stepFour,
  });

  useEffect(() => {
    if (!stepFour) return;
    reset(stepFour);
  }, [stepFour, reset]);

  const submitHandler = (data: PerkFormSchema['stepFour']) => {
    onSubmit('stepFour', data);
  };

  const values = useWatch({ control });

  useEffect(() => {
    const id = setTimeout(() => {
      patchStep({ stepFour: values });
    }, 500);

    return () => clearTimeout(id);
  }, [patchStep, values]);

  const claimType = useWatch({ control, name: 'claim.type' });
  const offerTypes = useWatch({ control, name: 'offerTypes' }) || [];

  return (
    <>
      <div className="invisible-y-scroll flex h-full flex-col gap-4">
        <FormRadioGroup
          className="flex-col gap-4"
          control={control}
          label="Claim Type"
          name="claim.type"
        >
          {perkClaimTypeMap.map((variant) => (
            <div
              className={twMerge(
                'flex flex-col gap-3 rounded-md !border border-gray-100 bg-white p-1',
                claimType === variant && 'border-transparent bg-brand-25',
              )}
              key={variant}
            >
              <FormRadio
                className="flex items-center justify-start gap-3 rounded-md bg-white px-3 py-2 data-[checked]:border-transparent data-[checked]:bg-brand-25 data-[checked]:pb-0"
                control={control}
                name="claim.type"
                value={variant}
              >
                <div
                  className={twMerge(
                    'flex size-4 items-center justify-center rounded-full border border-gray-300',
                    claimType === variant && 'border-transparent bg-brand-700',
                  )}
                >
                  {claimType === variant && <CheckIcon className="size-2" iconColor="#ffffff" />}
                </div>
                <span className="text-nowrap text-sm font-[450] text-gray-700">
                  {perkClaimTypeTranslationKey[variant]}
                </span>
              </FormRadio>
              {claimType === variant && claimSubContent[claimType]({ control })}
            </div>
          ))}
        </FormRadioGroup>

        <div className="flex flex-col gap-4">
          <span className="text-xs font-[450] text-gray-500">Offer Type (optional)</span>
          <div className="flex flex-col gap-3 rounded-md border border-gray-100 p-3">
            {perkOfferTypeOrderMap.map((variant) => (
              <Button
                className="text-sm-font-[450] flex justify-start gap-3 text-gray-700"
                disabledStyle={ButtonDisableStyle.NONE}
                key={variant}
                onClick={() => {
                  const payload = offerTypes?.includes(variant)
                    ? offerTypes.filter((type) => type !== variant)
                    : offerTypes?.concat(variant);

                  setValue('offerTypes', payload);
                }}
                styleType="NONE"
              >
                <div
                  className={twMerge(
                    'flex size-4 items-center justify-center rounded-[4px] border border-gray-300',
                    offerTypes?.includes(variant) && 'border-transparent bg-brand-700',
                  )}
                >
                  {offerTypes?.includes(variant) && (
                    <CheckIcon className="size-2" iconColor="#ffffff" />
                  )}
                </div>
                <span className="text-sm font-[450] text-gray-700">
                  {perkOfferTranslationKey[variant]}
                </span>
              </Button>
            ))}
          </div>
        </div>
      </div>

      <FormControlButtons
        disabled={!isValid}
        handleNext={handleSubmit(submitHandler)}
        handlePrev={handlePrevStep}
      />
    </>
  );
};
